import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AgentHubService from 'services/AgentHubService';
import { AlertRecipient, Area } from './types';

const service = new AgentHubService();

/**
 * The hub's areas (e.g. Meta, Google, creative, store and offer, pipelines) and who owns each, for every agent: an
 * agent whose work needs a person in an area @-mentions its people, and anything else goes to that agent's alert
 * recipients. An area is added on its own, then people are assigned to it (any number). Applied by every hub live.
 */
export default function Areas() {
  const [areas, setAreas] = useState<Area[] | null>(null);
  const [members, setMembers] = useState<AlertRecipient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // the area being saved, or 'new' while one is added
  const [name, setName] = useState('');

  useEffect(() => {
    let live = true;
    service.areas().then(a => live && setAreas(a)).catch(e => live && setError(`Areas not loaded: ${e.message}`));
    service.slackMembers().then(m => live && setMembers(m)).catch(e => live && setPeopleError(`Slack people not loaded: ${e.message}`));
    return () => { live = false; };
  }, []);

  const replace = (a: Area) => setAreas(all => [...(all ?? []).filter(x => x.id !== a.id), a].sort((x, y) => x.name.localeCompare(y.name)));
  const run = (key: string, p: Promise<void>) => {
    setBusy(key);
    setError(null);
    p.catch(e => setError(`Not saved: ${e.message}`)).finally(() => setBusy(null));
  };
  const add = () => run('new', service.addArea(name.trim()).then(a => { replace(a); setName(''); }));
  const assign = (a: Area, people: AlertRecipient[]) => run(a.id, service.setAreaPeople(a.id, people).then(replace));
  const remove = (a: Area) => {
    if (!window.confirm(`Remove the area "${a.name}"${a.people.length ? ` and its ${a.people.length} owner(s)` : ''}?`)) return;
    run(a.id, service.removeArea(a.id).then(() => setAreas(all => (all ?? []).filter(x => x.id !== a.id))));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Areas and owners</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        For every agent: when its work needs a person in an area, it @-mentions that area's owners. Anything else goes to the agent's "Alerts go to".
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {peopleError && <Alert severity="error" sx={{ mb: 1 }}>{peopleError}</Alert>}
      {!areas && !error && <CircularProgress size={18} />}
      {areas && (
        <>
          {!areas.length && <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>No areas yet: every agent's alerts go to its "Alerts go to".</Typography>}
          {areas.map(a => (
            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ width: 200, fontWeight: 600, flexShrink: 0 }}>{a.name}</Typography>
              <Autocomplete multiple size="small" sx={{ flex: 1, maxWidth: 560 }} options={members ?? []} loading={!members && !peopleError} value={a.people}
                disabled={busy === a.id} onChange={(_e, v) => assign(a, v)} getOptionLabel={o => o.name} isOptionEqualToValue={(o, v) => o.slackId === v.slackId}
                renderTags={(v, getTagProps) => v.map((p, i) => <Chip size="small" label={p.name} {...getTagProps({ index: i })} key={p.slackId} />)}
                renderInput={params => <TextField {...params} placeholder={a.people.length ? '' : 'Assign people'} />} />
              {busy === a.id ? <CircularProgress size={16} /> : <IconButton size="small" aria-label={`Remove the area ${a.name}`} onClick={() => remove(a)}><CloseIcon fontSize="small" /></IconButton>}
            </Box>
          ))}
          <Box component="form" sx={{ display: 'flex', gap: 1, mt: 1.5 }} onSubmit={e => { e.preventDefault(); if (name.trim()) add(); }}>
            <TextField size="small" placeholder="New area, e.g. Meta" value={name} onChange={e => setName(e.target.value)} sx={{ width: 220 }} inputProps={{ maxLength: 60 }} />
            <Button size="small" variant="contained" type="submit" disabled={!name.trim() || busy === 'new'}>{busy === 'new' ? 'Adding…' : 'Add area'}</Button>
          </Box>
        </>
      )}
    </Box>
  );
}
